import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { setStorageItem } from "../../utils/sessionStorage";
import { useUserStore } from "../../store/userStore";
import OTPSection from "../../components/OTPSection";
import { motion, AnimatePresence } from "framer-motion";
import ReCAPTCHA from "react-google-recaptcha";
import { API } from "../../utils/axios";
import Loading from "../../components/loader";
import BrandLogo from "../../assets/img/logo/buddha.png";
import { ArrowLeft } from "lucide-react";

export default function RegisterAccount() {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneNoError, setphoneNoError] = useState("");
  const [showOTP, setShowOTP] = useState(false);
  const [loading, setLoading] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [error, setError] = useState(false);
  const [isOTPLoading, setIsOTPLoading] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const lastLoggedInTime = new Date().toISOString();
  const updateUserDetails = useUserStore((state) => state.fullUpdate);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validatePhone = (phone: string) => {
    const regex = /^[1-9]\d{9}$/;
    return regex.test(phone);
  };

  const handleEmailChange = (e: any) => {
    const value = e.target.value;
    setEmail(value);

    if (value === "") {
      setEmailError("Email is required");
    } else if (!validateEmail(value)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  const handlePhoneNoChange = (e: any) => {
    const value = e.target.value;
    setPhone(value);

    if (value === "") {
      setphoneNoError("Phone number is required");
    } else if (!validatePhone(value)) {
      setphoneNoError("Please enter a valid 10-digit phone number");
    } else {
      setphoneNoError("");
    }
  };

  function captchaChange(value: any) {
    setCaptchaToken(value);
  }

  const changeForm = () => {
    setIsLogin(!isLogin);
  };

  const handleUserRegistration = async () => {
    try {
      if (!smsConsent) {
        setError(true);
        return;
      }
      if (!captchaToken) {
        toast.error("Please verify the CAPTCHA");
        return;
      }
      if (emailError) {
        toast.error("Invalid Email");
        return;
      }
      if (phoneNoError) {
        toast.error("Invalid Phone Number");
        return;
      }
      if (!firstName || !lastName || !email || !phone) {
        toast.error("All Fields are required");
        return;
      }
      setLoading(true);
      const userPayload = {
        firstName,
        lastName,
        email,
        phone,
        token: captchaToken,
      };

      const res = await API.post("/user", { ...userPayload });

      if (!res.data.success) {
        toast.error(res.data.msg);
        return;
      } else if (res.status === 201 || res.status === 200) {
        setUserData(res.data?.data);
        toast.success(res.data.msg);
        setShowOTP(true);
        setStorageItem("lastLoggedIn", lastLoggedInTime);
      } else {
        toast.error("Failed to create account! Please try once again:");
        console.error("Error! Please try once again:", res.data.message);
      }
    } catch (error) {
      console.error("handleUserRegistration Error:", error);
      toast.error("Failed to create account! Please try once again.");
    } finally {
      setLoading(false);
    }
  };

  const handleUserLogin = async () => {
    try {
      if (!email) {
        toast.error("Email is required");
        return;
      }

      if (emailError) {
        toast.error("Invalid Email");
        return;
      }
      setLoading(true);
      const res = await API.post("/user/login", {
        email,
      });
      if (res.status === 200) {
        toast.success(res.data.msg);
        setUserData(res.data?.data);
        setShowOTP(true);
        setStorageItem("lastLoggedIn", lastLoggedInTime);
      } else {
        toast.error("Something went wrong, please try again");
      }
    } catch (error: any) {
      if (error.response) {
        toast.error("Something went wrong, please try again");
      }
      console.error("handleAccountLogin Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOTPVerification = async (otp: string) => {
    setIsOTPLoading(true);
    try {
      let OTPPayload = {
        method: "POST",
        url:
          global.config.ROOTURL.prod +
          `/user/${
            isLogin ? "verify-otp-for-signin" : "verify-otp-for-signup"
          }`,
        data: {
          email: userData?.email || email,
          otp: otp,
        },
      };

      await API.post(
        `/user/${isLogin ? "verify-otp-for-signin" : "verify-otp-for-signup"}`,
        {
          email: userData?.email || email,
          otp: otp,
        },
      )
        .then((res) => {
          toast.success(res.data.msg);
          setStorageItem("uid", userData.id);
          setStorageItem("firstName", userData.firstName);
          setStorageItem("lastName", userData.lastName);
          setStorageItem("email", userData.email);
          setStorageItem("phoneNo", userData.phone);
          setStorageItem("user-data", JSON.stringify(userData));
          updateUserDetails({
            id: userData?.id,
            firstName: userData?.firstName,
            lastName: userData?.lastName,
            email: userData?.email,
            phone: userData?.phone,
            city: res?.data?.data?.address?.city,
            cityId: parseInt(res?.data?.data?.address?.cityId, 10),
            state: res?.data?.data?.address?.state,
            stateId: parseInt(res?.data?.data?.address?.stateId, 10),
            address: res?.data?.data?.address?.address_1,
            zipcode: res?.data?.data?.address?.postcode,
          });
          setUserData(null);

          setFirstName("");
          setLastName("");
          setEmail("");
          setPhone("");

          setTimeout(() => {
            navigate(-1);
          }, 1000);
        })
        .catch((error) => {
          if (error.response) {
            toast.error(error.response.data?.msg);
          }
          setIsOTPLoading(false);
          console.error("handleOTPVerification Error", error);
        });
    } catch (error: any) {
      if (error.response) {
        toast.error(error.response.data?.msg);
      }
      console.error("Invalid OTP. Please try again.");
    } finally {
      setIsOTPLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      if (!email) {
        toast.error("Invalid Email");
        return;
      }
      const res = await API.post("/user/resendOTP", {
        email,
      });
      if (res.status === 200) {
        toast.success(res.data.msg);
      } else {
        toast.error("Something went wrong, please try again");
      }
    } catch (error: any) {
      if (error.response) {
        toast.error("Something went wrong, please try again");
      }
      console.error("handleAccountLogin Error:", error);
    }
  };

  const handleBackToRegistration = () => {
    setShowOTP(false);
    setUserData(null);
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
  };

  return (
    <>
      <div className="relative min-h-screen grid grid-cols-1 lg:grid-cols-3 overflow-hidden bg-gray-50">
        <button
          className=" absolute top-5 left-5 z-50 flex items-center text-base gap-1 bg-black px-3 py-1 text-white rounded-lg"
          onClick={() => navigate("/")}
        >
          <ArrowLeft size={14} />
          Home
        </button>
        {/* Left Section - Forms */}
        <div className="flex flex-col col-span-3 items-center justify-center p-6 sm:p-12 lg:p-2 bg-white shadow-xl z-10 overflow-y-auto">
          <AnimatePresence mode="wait">
            {!showOTP ? (
              <motion.div
                key="auth-forms"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="w-full max-w-md space-y-3"
              >
                {/* Header Section */}
                <div className="text-center space-y-1">
                  <div className="flex flex-col items-center">
                    {!isLogin ? (
                      <div>
                        <img
                          src={BrandLogo}
                          className="h-20 w-20 mb-5 sm:mb-2 cursor-pointer"
                          onClick={() => navigate("/")}
                        />
                      </div>
                    ) : null}
                    <h1 className="text-3xl font-semibold text-gray-900 tracking-tight">
                      {isLogin ? "Welcome Back" : "Create Account"}
                    </h1>
                    <p className="text-lg text-gray-500">
                      {isLogin
                        ? "Sign in to access your dashboard"
                        : "Join us and start your wellness journey"}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-base text-gray-500">
                      {isLogin
                        ? "Don't have an account yet?"
                        : "Already have an account?"}
                      <button
                        onClick={changeForm}
                        className="ml-2 font-semibold text-teal-600 hover:text-teal-800 transition-colors focus:outline-none"
                      >
                        {isLogin ? "Create account" : "Sign in"}
                      </button>
                    </p>
                  </div>
                </div>

                {/* Form Section */}
                <div className="space-y-6">
                  <AnimatePresence mode="wait">
                    {!isLogin ? (
                      <motion.div
                        key="register"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.3 }}
                        className="space-y-4"
                      >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-1">
                            <label
                              htmlFor="firstName"
                              className="block text-sm font-semibold text-gray-700"
                            >
                              First Name
                            </label>
                            <input
                              type="text"
                              id="firstName"
                              value={firstName}
                              placeholder="John"
                              onChange={(e) => setFirstName(e.target.value)}
                              className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                            />
                          </div>
                          <div className="space-y-1">
                            <label
                              htmlFor="lastName"
                              className="block text-sm font-semibold text-gray-700"
                            >
                              Last Name
                            </label>
                            <input
                              type="text"
                              id="lastName"
                              value={lastName}
                              placeholder="Doe"
                              onChange={(e) => setLastName(e.target.value)}
                              className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
                            />
                          </div>
                        </div>

                        <div className="space-y-1">
                          <label
                            htmlFor="email"
                            className="block text-sm font-semibold text-gray-700"
                          >
                            Email Address
                          </label>
                          <input
                            type="email"
                            id="email"
                            value={email}
                            placeholder="john@example.com"
                            onChange={handleEmailChange}
                            className={`w-full p-3 bg-gray-50 border rounded-xl focus:ring-2 focus:border-transparent transition-all outline-none ${
                              emailError
                                ? "border-red-500 focus:ring-red-200"
                                : "border-gray-200 focus:ring-blue-500"
                            }`}
                          />
                          {emailError && (
                            <p className="text-red-500 text-xs mt-1">
                              {emailError}
                            </p>
                          )}
                        </div>

                        <div className="space-y-1">
                          <label
                            htmlFor="phone"
                            className="block text-sm font-semibold text-gray-700"
                          >
                            Mobile Number
                          </label>
                          <input
                            type="tel"
                            id="phone"
                            value={phone}
                            placeholder="1234567890"
                            onChange={handlePhoneNoChange}
                            className={`w-full p-3 bg-gray-50 border rounded-xl focus:ring-2 focus:border-transparent transition-all outline-none ${
                              phoneNoError
                                ? "border-red-500 focus:ring-red-200"
                                : "border-gray-200 focus:ring-blue-500"
                            }`}
                          />
                          {phoneNoError && (
                            <p className="text-red-500 text-xs mt-1">
                              {phoneNoError}
                            </p>
                          )}
                        </div>

                        <div className="py-2">
                          <ReCAPTCHA
                            sitekey={
                              process.env.REACT_APP_PROD ===
                              "https://lastminutewellness.ca/api"
                                ? "6LdpITosAAAAAHpC3c7IWXWxQoA2wTOTqS81fgbY"
                                : "6LdK2QgsAAAAAB6o1D7RRIjqBESxhFYERlm2870R"
                            }
                            onChange={captchaChange}
                          />
                        </div>

                        <div className="flex items-start mb-4">
                          <div className="flex items-center h-5">
                            <input
                              id="sms-consent"
                              type="checkbox"
                              className={`w-4 h-4 rounded cursor-pointer transition-colors
              ${
                error
                  ? "border-2 border-red-500 bg-red-50 ring-2 ring-red-200 focus:ring-red-500"
                  : "border-gray-300 text-blue-600 focus:ring-blue-500"
              }`}
                              checked={smsConsent}
                              onChange={(e) => {
                                setSmsConsent(e.target.checked);
                                if (e.target.checked) setError(false);
                              }}
                            />
                          </div>

                          <div className="ml-3 text-sm">
                            <label
                              htmlFor="sms-consent"
                              className={`text-sm font-medium  ${error ? "text-red-500" : "text-gray-500"}`}
                            >
                              I agree to receive automated SMS,
                              booking confirmations, and appointment reminders
                              from Last Minute Wellness.
                            </label>
                          </div>
                        </div>

                        {loading ? (
                          <div className="flex justify-center py-2">
                            <Loading size={40} />
                          </div>
                        ) : (
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            type="button"
                            onClick={handleUserRegistration}
                            className="w-full py-3.5 px-4 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white font-semibold rounded-xl shadow-sm shadow-emerald-500/30 transition-all duration-200"
                          >
                            Create Account
                          </motion.button>
                        )}
                      </motion.div>
                    ) : (
                      <motion.div
                        key="login"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                        className="space-y-5"
                      >
                        <div className="space-y-2">
                          <label
                            htmlFor="email"
                            className="block text-sm font-semibold text-gray-700"
                          >
                            Email Address
                          </label>
                          <input
                            type="email"
                            id="email"
                            value={email}
                            placeholder="john@example.com"
                            onChange={handleEmailChange}
                            className={`w-full p-3 bg-gray-50 border rounded-xl focus:ring-2 focus:border-transparent transition-all outline-none ${
                              emailError
                                ? "border-red-500 focus:ring-red-200"
                                : "border-gray-200 focus:ring-blue-500"
                            }`}
                          />
                          {emailError && (
                            <p className="text-red-500 text-xs mt-1">
                              {emailError}
                            </p>
                          )}
                        </div>

                        {loading ? (
                          <div className="flex justify-center py-3">
                            <Loading size={40} />
                          </div>
                        ) : (
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            type="button"
                            disabled={loading}
                            onClick={handleUserLogin}
                            className="w-full py-3.5 px-4 bg-gray-900 hover:bg-black text-white font-semibold rounded-xl shadow-lg transition-all duration-200"
                          >
                            Continue
                          </motion.button>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-4">
                      By continuing, you agree to LastMinuteWellness{" "}
                      <span
                        className="underline cursor-pointer"
                        onClick={() => navigate("/terms-of-service")}
                      >
                        Terms of Service
                      </span>{" "}
                      and{" "}
                      <span
                        className="underline cursor-pointer"
                        onClick={() => navigate("/privacy-policy")}
                      >
                        Privacy Policy
                      </span>
                      .
                    </p>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="otp"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4 }}
                className="w-full max-w-md"
              >
                <OTPSection
                  email={userData?.email || email}
                  phone={userData?.phone || phone}
                  onVerify={handleOTPVerification}
                  onBack={handleBackToRegistration}
                  isLoading={isOTPLoading}
                  handleResendOTP={handleResendOTP}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
}
